import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  OffcanvasBodyComponent,
  OffcanvasComponent,
  OffcanvasHeaderComponent
} from '@coreui/angular-pro';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { ConsentComponent, SafeHtmlPipe } from 'shared';

type Policy = 'privacy_policy' | 'terms_of_service' | 'legal_notice';

/**
 * Wraps every funnel page with:
 *   - card-stack background + dark overlay
 *   - top-right language switcher
 *   - bottom legal links (Privacy / TOS / Legal Notice) — open as offcanvas
 *   - "operated by Continental Capital Bank Ltd." strip (uses `login.footer` i18n)
 *
 * The offcanvas + policy-fetch flow mirrors libs/app/.../login-page so the UX
 * matches the rest of the app exactly.
 */
@Component({
  selector: 'app-funnel-shell',
  standalone: true,
  imports: [
    CommonModule,
    OffcanvasComponent,
    OffcanvasHeaderComponent,
    OffcanvasBodyComponent,
    ConsentComponent,
    SafeHtmlPipe,
    TranslatePipe
  ],
  templateUrl: './funnel-shell.component.html',
  styleUrls: ['./funnel-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelShellComponent {
  private i18n             = inject(I18nService);
  private translateService = inject(TranslateService);
  private router           = inject(Router);
  private location         = inject(Location);
  private http             = inject(HttpClient);
  private destroyRef       = inject(DestroyRef);

  readonly languages = [
    { code: 'en', label: 'EN' },
    { code: 'de', label: 'DE' }
  ];

  /** `login.footer` already contains the "operated by Continental Capital Bank Ltd." HTML. */
  readonly footerText = toSignal(
    this.translateService.translateAsync('login.footer'),
    { initialValue: null }
  );

  // Offcanvas state
  policyVisible    = signal(false);
  policyLoading    = signal(false);
  policyContent    = signal<string>('');

  currentLang(): string {
    return this.i18n.getLanguage() || 'en';
  }

  switchLanguage(code: string): void {
    if (code === this.currentLang()) return;
    this.i18n.setLanguage(code, false);

    const url = this.router.url;
    const swapped = url.replace(/^\/[a-z]{2}(\/|$)/, `/${code}$1`);
    this.location.replaceState(swapped);
  }

  openPolicy(policy: Policy): void {
    this.openDocument(`/media/${this.currentLang()}/policy/${policy}?embed=true`);
  }

  /**
   * Opens any policy document in the same slide-in panel — e.g. the documents the
   * direct checkout asks the buyer to accept, whose `url` the backend supplies
   * (already `?embed=true`, already in the language it is served in).
   */
  openDocument(url: string): void {
    this.policyLoading.set(true);
    this.policyVisible.set(true);
    this.policyContent.set('');

    this.http.get(url, { responseType: 'text' as 'text' })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (content) => {
        this.policyContent.set(content || '');
        this.policyLoading.set(false);
      },
      error: () => {
        this.policyContent.set('<p>Could not load the policy. Please try again.</p>');
        this.policyLoading.set(false);
      }
    });
  }

  closePolicy(): void {
    this.policyVisible.set(false);
  }

  policyVisibleChange(visible: boolean): void {
    this.policyVisible.set(visible);
  }
}
